import React, { useState, useEffect } from 'react';
import {
    Pane, Card, Text, Button, Heading, Spinner, Table,
    Alert, Strong, Badge, TextInputField, Select, Tooltip, InfoSignIcon,
    CaretUpIcon, CaretDownIcon
} from 'evergreen-ui';
import { CSVLink } from "react-csv";
import { ForeignKeyMultiSelectDropDownInput } from '../../../inputs/ForeignKeyInputs';
import { formatCurrency } from '../../../util';
import { generateProfitLossPDF } from '../ProfitLossReport';
import TimePeriodSelector from '../components/TimePeriodSelector';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import QUERY_PRODUCTS_FOR_DROPDOWN from '../../../graphql/queries/Products/QueryProductsForDropdown';
import { useSelector } from 'react-redux';

const ProfitLossReport = ({
    startDate,
    endDate,
    selectedPeriod,
    selectedProducts,
    handlePeriodChange,
    setStartDate,
    setEndDate,
    setSelectedPeriod,
    productsLoading,
    productsData,
    handleProductChange,
    details,
    setDetails,
    localData,
    queryLoading,
    getCSVData,
    handleSort,
    sortConfig,
    getSortedData,
    revenue,
    expenses,
    netProfit,
    periods = [
        { label: 'Custom', value: 'custom' },
        { label: 'Month to date', value: 'mtd' },
        { label: 'Quarter to date', value: 'qtd' },
        { label: 'Last Quarter', value: 'lastq' },
        { label: 'Year to date', value: 'ytd' },
        { label: 'Last Year', value: 'lasty' }
    ],
    timePeriodSelector
}) => {
    const currentpublisher = useSelector((state) => state.currentpublisher.value);
    const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);
    const currencySymbol = publisherGlobalData?.currency?.symbol || '$';

    const { data: productsDataFromQuery } = useQuery(QUERY_PRODUCTS_FOR_DROPDOWN, {
        variables: {
            publisher: parseInt(currentpublisher?.id)
        },
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-only',
        onCompleted: (data) => {
            console.log('Raw products data from query:', data);
        }
    });

    console.log('Products data from query (detailed):',
        productsDataFromQuery?.products?.map(product => ({
            id: product.id,
            title: product.productWork?.title,
            format: product.format,
            isbn: product.isbn
        }))
    );

    const productOptions = [
        {
            id: 'all',
            key: 'all',
            value: '- ALL -',
            label: '- ALL -',
            description: 'Show all products'
        },
        ...(productsDataFromQuery?.products?.map(product => {
            return {
                id: product.id.toString(),
                key: product.id.toString(),
                value: product.productWork?.title,
                label: `${product.productWork?.title} (${product.format && product.format.description ? product.format.description : 'No format'})`,
                description: product.isbn || '',
                format: product.format
            };
        }) || [])
    ];

    console.log('Final product options:', productOptions);

    const calculateMonthlyPL = (item) => {
        const publicationDate = new Date(item.publicationDate);
        const now = new Date();

        if (!publicationDate || isNaN(publicationDate.getTime())) {
            return 0;
        }

        const monthsDiff = (now.getFullYear() - publicationDate.getFullYear()) * 12 +
                          (now.getMonth() - publicationDate.getMonth());
        const totalMonths = Math.max(1, monthsDiff);
        const totalPL = (item.revenue || 0) - (item.expenses || 0);

        return totalPL / totalMonths;
    };

    // Helper function to format currency with the publisher's currency symbol
    const formatCurrencyWithSymbol = (amount) => {
        return formatCurrency(amount, 'USD', currencySymbol);
    };

    return (
        <Card backgroundColor="white" elevation={1} padding={24} marginBottom={32}>
            {/* Time Period and Product Selection */}
            {timePeriodSelector}

            {/* Product Selection */}
            <Pane flex={2} maxWidth={400}>
                <Text fontWeight={500} marginBottom={8}>Select Products</Text>
                <ForeignKeyMultiSelectDropDownInput
                    loading={productsLoading}
                    field="product"
                    values={productOptions}
                    value={selectedProducts}
                    handleChange={handleProductChange}
                    el="product"
                    formLabel="Products"
                    details={details}
                    setDetails={setDetails}
                    popoverProps={{
                        minWidth: "400px",
                        maxWidth: "400px"
                    }}
                />
            </Pane>

            {/* Loading State */}
            {queryLoading && (
                <Pane display="flex" alignItems="center" justifyContent="center" marginY={32}>
                    <Spinner />
                    <Text marginLeft={8}>Generating report...</Text>
                </Pane>
            )}

            {/* Report Content */}
            {localData?.profitLoss && (
                <Card backgroundColor="white" elevation={1} padding={24} marginBottom={32}>
                    {/* Header and Buttons in one row */}
                    <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={16}>
                        <Heading size={500}>Report Summary</Heading>

                        <Pane display="flex" gap={8}>
                            <Button
                                appearance="primary"
                                onClick={() => generateProfitLossPDF(
                                    {
                                        revenue: localData?.profitLoss?.revenue,
                                        expenses: localData?.profitLoss?.expenses,
                                        netProfit: localData?.profitLoss?.netProfit,
                                        revenueByProduct: localData?.profitLoss?.revenueByProduct,
                                        expensesByCategory: localData?.profitLoss?.expensesByCategory,
                                    },
                                    startDate,
                                    endDate,
                                    selectedProducts,
                                    currencySymbol
                                )}
                                height={32}
                                className="evergreen-button"
                                style={{ backgroundColor: 'var(--maroon)', border: 'none' }}
                            >
                                Print Report
                            </Button>
                            <CSVLink
                                data={getCSVData()}
                                filename={`profit_loss_report_${startDate}_to_${endDate}.csv`}
                                className="evergreen-button"
                            >
                                <Button
                                    appearance="primary"
                                    intent="success"
                                    style={{ backgroundColor: 'var(--maroon)', border: 'none' }}
                                >
                                    Download CSV
                                </Button>
                            </CSVLink>
                        </Pane>
                    </Pane>

                    <Table>
                        <Table.Head>
                            <Table.TextHeaderCell
                                onClick={() => handleSort('title')}
                                cursor="pointer"
                            >
                                Title
                                {sortConfig.key === 'title' && (
                                    sortConfig.direction === 'ascending' ? <CaretUpIcon /> : <CaretDownIcon />
                                )}
                            </Table.TextHeaderCell>
                            <Table.TextHeaderCell
                                onClick={() => handleSort('isbn')}
                                cursor="pointer"
                            >
                                ISBN
                                {sortConfig.key === 'isbn' && (
                                    sortConfig.direction === 'ascending' ? <CaretUpIcon /> : <CaretDownIcon />
                                )}
                            </Table.TextHeaderCell>
                            <Table.TextHeaderCell
                                onClick={() => handleSort('formatType')}
                                cursor="pointer"
                            >
                                Type
                                {sortConfig.key === 'formatType' && (
                                    sortConfig.direction === 'ascending' ? <CaretUpIcon /> : <CaretDownIcon />
                                )}
                            </Table.TextHeaderCell>
                            <Table.TextHeaderCell
                                onClick={() => handleSort('retailPrice')}
                                cursor="pointer"
                                textAlign="right"
                            >
                                Retail Price
                                {sortConfig.key === 'retailPrice' && (
                                    sortConfig.direction === 'ascending' ? <CaretUpIcon /> : <CaretDownIcon />
                                )}
                            </Table.TextHeaderCell>
                            <Table.TextHeaderCell
                                onClick={() => handleSort('units')}
                                cursor="pointer"
                                textAlign="right"
                            >
                                Quantity Sold
                                {sortConfig.key === 'units' && (
                                    sortConfig.direction === 'ascending' ? <CaretUpIcon /> : <CaretDownIcon />
                                )}
                            </Table.TextHeaderCell>
                            <Table.TextHeaderCell
                                onClick={() => handleSort('revenue')}
                                cursor="pointer"
                                textAlign="right"
                            >
                                Revenue
                                {sortConfig.key === 'revenue' && (
                                    sortConfig.direction === 'ascending' ? <CaretUpIcon /> : <CaretDownIcon />
                                )}
                            </Table.TextHeaderCell>
                            <Table.TextHeaderCell
                                onClick={() => handleSort('expenses')}
                                cursor="pointer"
                                textAlign="right"
                            >
                                Expenses
                                {sortConfig.key === 'expenses' && (
                                    sortConfig.direction === 'ascending' ? <CaretUpIcon /> : <CaretDownIcon />
                                )}
                            </Table.TextHeaderCell>
                            <Table.TextHeaderCell
                                onClick={() => handleSort('profit')}
                                cursor="pointer"
                                textAlign="right"
                            >
                                Profit in period
                                {sortConfig.key === 'profit' && (
                                    sortConfig.direction === 'ascending' ? <CaretUpIcon /> : <CaretDownIcon />
                                )}
                            </Table.TextHeaderCell>
                            <Table.TextHeaderCell
                                onClick={() => handleSort('monthlyPL')}
                                cursor="pointer"
                                textAlign="right"
                            >
                                Monthly P&L
                                <Tooltip content="This is a calculation of the total revenue, divided by total expenses since the publication date of the product, by month. It should help you figure out how much this product is adding to your bottom line each month.">
                                    <InfoSignIcon size={12} marginLeft={4} />
                                </Tooltip>
                                {sortConfig.key === 'monthlyPL' && (
                                    sortConfig.direction === 'ascending' ? <CaretUpIcon /> : <CaretDownIcon />
                                )}
                            </Table.TextHeaderCell>
                        </Table.Head>
                        <Table.Body>
                            {getSortedData(localData)?.map(item => {
                                const itemRevenue = item.revenue || 0;
                                const itemExpenses = item.expenses || 0;
                                const itemProfit = itemRevenue - itemExpenses;
                                // const authorString = item.authors && item.authors.length > 0 ? item.authors.join(', ') : '';

                                return (
                                    <Table.Row key={item.productId}>
                                        <Table.TextCell>
                                            <RouterLink
                                                to={`/products/${item.productId}`}
                                                style={{
                                                    textDecoration: 'none',
                                                    color: 'var(--maroon)',
                                                    transition: 'color 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => e.target.style.color = 'darkgreen'}
                                                onMouseLeave={(e) => e.target.style.color = 'var(--maroon)'}
                                            >
                                                <Pane>
                                                    <Text display="block" cursor="pointer" style={{ color: 'inherit', fontWeight: 500 }}>
                                                        {item.productWork?.title || item.title || 'Untitled Product'}
                                                    </Text>
                                                    <Text display="block" size={300} color="muted" cursor="pointer" style={{ color: 'inherit', marginTop: '2px' }}>
                                                        {item.formatType || 'N/A'} / {item.isbn || 'N/A'}
                                                    </Text>
                                                </Pane>
                                            </RouterLink>
                                        </Table.TextCell>
                                        <Table.TextCell>{item.isbn || 'N/A'}</Table.TextCell>
                                        <Table.TextCell>{item.formatType || 'N/A'}</Table.TextCell>
                                        <Table.TextCell textAlign="right">{formatCurrencyWithSymbol(item.retailPrice || 0)}</Table.TextCell>
                                        <Table.TextCell textAlign="right">{item.units || 0}</Table.TextCell>
                                        <Table.TextCell textAlign="right">{formatCurrencyWithSymbol(itemRevenue)}</Table.TextCell>
                                        <Table.TextCell textAlign="right">{formatCurrencyWithSymbol(itemExpenses)}</Table.TextCell>
                                        <Table.TextCell textAlign="right" color={itemProfit < 0 ? 'red' : 'green'}>
                                            {formatCurrencyWithSymbol(itemProfit)}
                                        </Table.TextCell>
                                        <Table.TextCell textAlign="right" color={calculateMonthlyPL(item) < 0 ? 'red' : 'green'}>
                                            {formatCurrencyWithSymbol(calculateMonthlyPL(item))}
                                        </Table.TextCell>
                                    </Table.Row>
                                );
                            })}
                            <Table.Row intent="success">
                                <Table.TextCell><Strong>Totals</Strong></Table.TextCell>
                                <Table.TextCell>-</Table.TextCell>
                                <Table.TextCell>-</Table.TextCell>
                                <Table.TextCell>-</Table.TextCell>
                                <Table.TextCell textAlign="right">
                                    <Strong>{localData ? getSortedData(localData).reduce((sum, item) => sum + (item.units || 0), 0) : 0}</Strong>
                                </Table.TextCell>
                                <Table.TextCell textAlign="right">
                                    <Strong>{formatCurrencyWithSymbol(localData?.profitLoss?.revenue || 0)}</Strong>
                                </Table.TextCell>
                                <Table.TextCell textAlign="right">
                                    <Strong>{formatCurrencyWithSymbol(localData?.profitLoss?.expenses || 0)}</Strong>
                                </Table.TextCell>
                                <Table.TextCell textAlign="right">
                                    <Strong color={localData?.profitLoss?.netProfit < 0 ? 'red' : 'green'}>
                                        {formatCurrencyWithSymbol(localData?.profitLoss?.netProfit || 0)}
                                    </Strong>
                                </Table.TextCell>
                                <Table.TextCell textAlign="right">-</Table.TextCell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </Card>
            )}
        </Card>
    );
};

export default ProfitLossReport;
